import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Utils
import { GestureShakePlugin } from '../../../capacitor_plugins/gestureShake';
import { usePerformHapticFeedback, HapticsStyle } from '../../utils/haptics/usePerformHapticFeedback';
import useSheet from '../../mobile/structural/sheet/hooks/useSheet';
import useUpdatedRef from '../../utils/react/useUpdatedRef';
import { isPlatformNativeMobileApp } from '../../platform/utils/platformDetailsUtils';

// Selectors
import { isCurrentlyEditing } from '../../element/selection/currentlyEditingSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Actions
import { finishEditingCurrentElement } from '../../element/selection/selectionActions';

// Types
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';
import { CAPACITOR_GESTURE_SHAKE_PLUGIN } from '../../../capacitor_plugins/pluginConstants';

/**
 * A hook that attaches the gesture shake event listener to open the undo/redo menu.
 * This is separate from the component that renders the menu.
 */
export default function useAttachGestureShake() {
    const performHapticFeedback = usePerformHapticFeedback();
    const sheetProps = useSheet(SheetId.GestureShakeMenu);

    const dispatch = useDispatch();

    const isCurrentlyEditingElement = useSelector(isCurrentlyEditing);
    const dispatchFinishEditingCurrentElement = () => dispatch(finishEditingCurrentElement());

    const isCurrentlyEditingElementRef = useUpdatedRef(isCurrentlyEditingElement);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    useEffect(() => {
        {
            // gesture shake is only available on native mobile apps
            if (!isPlatformNativeMobileApp(platformDetails)) return;

            if (!Capacitor.isPluginAvailable(CAPACITOR_GESTURE_SHAKE_PLUGIN)) return;

            const handleShake = async () => {
                performHapticFeedback(HapticsStyle.ImpactMedium);

                if (isCurrentlyEditingElementRef.current) dispatchFinishEditingCurrentElement();

                sheetProps.dispatchOpenSheet();
            };

            const setupListeners = async () => {
                await GestureShakePlugin.addListener('shake', handleShake);
            };

            setupListeners();

            return () => {
                GestureShakePlugin.remove();
            };
        }
    }, []);
}
